import { Label, clx } from '@medusajs/ui';
import React, { useImperativeHandle, useState } from 'react';
import type { FieldError } from 'react-hook-form';

import Eye from '@modules/common/icons/eye';
import EyeOff from '@modules/common/icons/eye-off';

type InputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  label?: string;
  error?: FieldError;
  name: string;
  placeholder?: string;
  labelClassName?: string;
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type,
      name,
      label,
      error,
      required,
      placeholder,
      labelClassName,
      className,
      ...props
    },
    ref
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [showPassword, setShowPassword] = useState(false);

    // Derive input type from props and showPassword state
    const inputType = type === 'password' && showPassword ? 'text' : type;

    useImperativeHandle(ref, () => inputRef.current!);

    return (
      <div className="flex w-full flex-col">
        {label && (
          <Label
            className={clx('txt-compact-medium-plus mb-2', labelClassName)}
          >
            {label}
          </Label>
        )}

        <div className="txt-compact-medium relative z-0 flex w-full">
          <input
            type={inputType}
            name={name}
            placeholder={placeholder}
            required={required}
            className={clx(
              '!border-white-40 border-ui-border-base bg-ui-bg-field bg-white-10 placeholder:text-grey-10 hover:bg-ui-bg-field-hover focus:shadow-borders-interactive-with-active mt-0 block h-11 w-full appearance-none rounded-md border px-4 py-1 focus:outline-none focus:ring-0',
              {
                'border-red-60': error,
              },
              className
            )}
            {...props}
            ref={inputRef}
          />
          {type === 'password' && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-ui-fg-subtle focus:text-ui-fg-base absolute right-0 top-3 px-4 outline-none transition-all duration-150 focus:outline-none"
            >
              {showPassword ? <Eye /> : <EyeOff />}
            </button>
          )}
        </div>
        <p className="text-red-60 mt-1 text-xs">{error?.message}</p>
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
